import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Invoice } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Loader2, ArrowLeft, AlertCircle, Info } from 'lucide-react';

// Helper to format period for display
function formatPeriod(periodStart: string): string {
  const date = new Date(periodStart);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getInvoice(invoiceId!);
      setInvoice(response.invoice);
    } catch (err: unknown) {
      console.error('Failed to load invoice:', err);
      const apiError = err as { error?: string };
      setError(apiError.error || 'Failed to load invoice');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  if (error || !invoice) {
    return (
      <Layout>
        <div className="space-y-6">
          <Link to="/billing" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Billing
          </Link>

          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-600">{error || 'Invoice not found'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <Link to="/billing" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Billing
        </Link>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>Invoice {invoice.invoice_number}</CardTitle>
                <CardDescription className="mt-1">
                  Billing period: {formatPeriod(invoice.period_start)}
                </CardDescription>
              </div>
              <InvoiceStatusBadge status={invoice.status} />
            </div>
          </CardHeader>
          <CardContent>
            {/* Invoice Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Issued Date</p>
                <p className="font-medium">{formatDate(invoice.issued_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Wholesale Rate</p>
                <p className="font-medium">{Math.round(invoice.wholesale_percent_used * 100)}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total (Wholesale)</p>
                <p className="text-xl font-bold text-abby-primary">
                  {formatCurrency(invoice.total_wholesale)}
                </p>
              </div>
            </div>

            {/* Note */}
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2 text-blue-700 text-sm">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                This invoice is a snapshot of billed opportunities during the billing period.
                For questions, contact Abby Support with your invoice number.
              </p>
            </div>

            {/* Line Items */}
            {invoice.lines && invoice.lines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-3 font-medium">Account</th>
                      <th className="pb-3 font-medium">Product</th>
                      <th className="pb-3 font-medium">Close Date</th>
                      <th className="pb-3 font-medium text-right">Qty</th>
                      <th className="pb-3 font-medium text-right">Unit Price</th>
                      <th className="pb-3 font-medium text-right">Retail</th>
                      <th className="pb-3 font-medium text-right">Wholesale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invoice.lines.map((line) => (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="py-3 font-medium text-gray-900">
                          {line.customer_account_name}
                        </td>
                        <td className="py-3 text-gray-600">{line.product_name}</td>
                        <td className="py-3 text-gray-600">{formatDate(line.close_date)}</td>
                        <td className="py-3 text-right text-gray-600">
                          {line.quantity}
                        </td>
                        <td className="py-3 text-right text-gray-600">
                          {formatCurrency(line.unit_price)}
                        </td>
                        <td className="py-3 text-right text-gray-600">
                          {formatCurrency(line.retail_amount)}
                        </td>
                        <td className="py-3 text-right font-medium text-gray-900">
                          {formatCurrency(line.wholesale_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 font-medium">
                      <td colSpan={5} className="py-3 text-right">Totals:</td>
                      <td className="py-3 text-right">{formatCurrency(invoice.total_retail)}</td>
                      <td className="py-3 text-right text-abby-primary">
                        {formatCurrency(invoice.total_wholesale)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No line items on this invoice.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    issued: 'bg-blue-100 text-blue-800',
    sent: 'bg-purple-100 text-purple-800',
    paid: 'bg-green-100 text-green-800',
    void: 'bg-gray-100 text-gray-800',
  };

  const labels: Record<string, string> = {
    issued: 'Issued',
    sent: 'Sent',
    paid: 'Paid',
    void: 'Void',
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.issued}`}>
      {labels[status] || status}
    </span>
  );
}
